/**
 * Cashback coupons (kupon z vračilom v dobroimetju) on the web: which coupon
 * is one, how its value reads, and the admin form's shaping and preview.
 * Customer amounts always come from the server's cashback view; the preview
 * below is admin-only and labelled as such.
 */
import { AVAILABLE_CURRENCIES, formatCurrency } from "@configs/currencies";
import { centsToUnitsInput, currencySuffix } from "./admin-money";
import {
  CASHBACK_ADMIN_CURRENCIES,
  MAX_GRANT_CENTS,
  type CashbackAdminCurrency,
} from "./cashback-admin";
import { couponCodeAsUsed } from "./bundles";

export type CouponRewardType = "DISCOUNT" | "CASHBACK";
export const CASHBACK_COUPON_CURRENCIES = CASHBACK_ADMIN_CURRENCIES;

/**
 * The create form's currencies once CASHBACK is chosen: exactly one the
 * Valuta select offers (default eur), so the select is never blank and the
 * payload always carries the one currency the backend requires.
 */
export function cashbackFormCurrencies(
  currencies: string[] | null | undefined,
): string[] {
  return [toCashbackCurrency(currencies?.[0])];
}

export function isCashbackCoupon(c: unknown): boolean {
  return (
    (c as { rewardType?: unknown } | null | undefined)?.rewardType ===
    "CASHBACK"
  );
}

/** "10 %" or "5,00 €": the coupon's value, without wording around it. */
export function couponValueLabel(
  c: { type?: string | null; discount?: number | string | null },
  fmt: (cents: number) => string,
  locale: string,
): string {
  if (c.type === "PERCENTAGE")
    return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(Number(c.discount) || 0)} %`;
  return fmt(Math.round(Number(c.discount) || 0));
}

/** "JESEN10 (vračilo v dobroimetju)" for a cashback coupon, the bare code otherwise. */
export function couponCodeWithSuffix(
  coupon: unknown,
  suffix: string,
): string | null {
  const code = (coupon as { code?: string | null } | null | undefined)?.code;
  if (!code) return null;
  return isCashbackCoupon(coupon) ? `${code} ${suffix}` : code;
}

export function toCashbackCurrency(value: unknown): CashbackAdminCurrency {
  const v = String(value ?? "")
    .toLowerCase()
    .split("-")[0];
  return (CASHBACK_ADMIN_CURRENCIES as readonly string[]).includes(v)
    ? (v as CashbackAdminCurrency)
    : "eur";
}

export type CashbackCouponFigures = {
  type?: string | null;
  discount?: number | string | null;
  minimumAmount?: number | string | null;
  maximumDiscountAmount?: number | string | null;
};

/** The backend's reward rule (cashback-math rewardCents) on the form's numbers. */
export function cashbackCouponPreviewCents(
  c: CashbackCouponFigures,
  qualifyingCents: number,
  currency: CashbackAdminCurrency,
): number {
  const minimum = Math.max(0, Math.round(Number(c.minimumAmount) || 0));
  if (qualifyingCents <= 0 || qualifyingCents < minimum) return 0;
  const ceiling = MAX_GRANT_CENTS[currency];
  if (c.type === "PERCENTAGE") {
    const rateBp = Math.round((Number(c.discount) || 0) * 100);
    const max = Number(c.maximumDiscountAmount);
    const cap =
      Number.isFinite(max) && max > 0
        ? Math.round(max)
        : Number.POSITIVE_INFINITY;
    return Math.max(
      0,
      Math.min(Math.floor((qualifyingCents * rateBp) / 10_000), cap, ceiling),
    );
  }
  return Math.max(
    0,
    Math.min(Math.round(Number(c.discount) || 0), qualifyingCents, ceiling),
  );
}

const PREVIEW_BASIS: Record<CashbackAdminCurrency, number> = {
  eur: 6000,
  pln: 25000,
  czk: 150000,
  ron: 30000,
};

/**
 * The admin preview, built from the coupon's own minimum so it shows whether
 * the minimum is right: "Kupec, ki kupi za 40,00 € (najnižji znesek), dobi
 * 4,00 € dobroimetja. Vračilo je največ 10,00 € na naročilo." — or null while
 * the form says nothing yet. Without a minimum the example is a typical cart.
 */
export function cashbackCouponPreviewSentence(
  c: CashbackCouponFigures,
  currency: CashbackAdminCurrency,
  fmt: (cents: number) => string,
): string | null {
  const minimum = Math.max(0, Math.round(Number(c.minimumAmount) || 0));
  const basis = minimum > 0 ? minimum : PREVIEW_BASIS[currency];
  const reward = cashbackCouponPreviewCents(c, basis, currency);
  if (reward <= 0) return null;
  const max = Number(c.maximumDiscountAmount);
  const cap =
    c.type === "PERCENTAGE" && Number.isFinite(max) && max > 0
      ? ` Vračilo je največ ${fmt(Math.round(max))} na naročilo.`
      : "";
  const floor =
    minimum > 0 ? " (najnižji znesek)" : " (kupon nima najnižjega zneska)";
  return `Kupec, ki kupi za ${fmt(basis)}${floor}, dobi ${fmt(reward)} dobroimetja.${cap}`;
}

/** The admin coupon form's values (create and edit share them). */
export type CouponFormValues = {
  validUntil: { toISOString(): string } | null;
  code: string;
  discount: number | string;
  type: string;
  description?: string;
  minimumAmount?: number;
  maximumDiscountAmount?: number;
  freeShipping: boolean;
  minimumAmountShipping?: number;
  currencies: string[];
  rewardType: CouponRewardType;
  cashbackLifetimeDays?: number | string;
};

/**
 * The body for POST (creating) or PATCH. A DISCOUNT coupon is sent exactly as
 * the form always sent it. A CASHBACK coupon: one currency, no free shipping,
 * no cap when FIXED, its lifetime; the reward type only on create (it cannot
 * change, spec D2).
 */
export function couponWritePayload(
  data: CouponFormValues,
  creating: boolean,
): Record<string, unknown> {
  const base: Record<string, unknown> = {
    validUntil: data.validUntil!.toISOString(),
    code: data.code,
    discount: Number(data.discount),
    type: data.type,
    // Create and Update DTOs require description, minimumAmount and
    // minimumAmountShipping (IsString/IsNumber, not optional): an empty field
    // is "" / 0, never omitted, or the save is a 400.
    description: data.description ?? "",
    minimumAmount: Number(data.minimumAmount) || 0,
    maximumDiscountAmount: data.maximumDiscountAmount
      ? Number(data.maximumDiscountAmount)
      : undefined,
    freeShipping: data.freeShipping,
    minimumAmountShipping: Number(data.minimumAmountShipping) || 0,
    // An edit merges over the stored row: an emptied list must be sent as []
    // ("all currencies"), or the old list survives.
    currencies:
      data.currencies && data.currencies.length > 0
        ? data.currencies
        : creating
          ? undefined
          : [],
  };
  if (data.rewardType !== "CASHBACK") return base;
  return {
    ...base,
    ...(creating ? { rewardType: "CASHBACK" } : {}),
    freeShipping: false,
    minimumAmountShipping: 0,
    // An edit merges over the stored row, so an omitted cap would survive:
    // null clears it (FIXED has none; an emptied PERCENTAGE cap means no cap).
    maximumDiscountAmount:
      data.type === "PERCENTAGE" && base.maximumDiscountAmount !== undefined
        ? base.maximumDiscountAmount
        : creating
          ? undefined
          : null,
    currencies: data.currencies?.length ? [data.currencies[0]] : undefined,
    cashbackLifetimeDays: Number(data.cashbackLifetimeDays) || 90,
  };
}

/** The value field's label: the unit is in the field (%, € …), never cents. */
export function discountFieldLabel(cashback: boolean, type: string): string {
  const what = cashback ? "Vračilo" : "Popust";
  return type === "PERCENTAGE" ? `${what} (%)` : `${what} (znesek)`;
}

/**
 * A coupon amount (cents) in the list and detail view: "3,00 €" for a
 * one-currency coupon. A coupon for several currencies (or all) applies the
 * same number in each, so it reads "3,00 € / Kč / zł / Lei".
 */
export function couponMoneyLabel(
  cents: number | string | null | undefined,
  currencies: string[] | null | undefined,
): string {
  if (cents === null || cents === undefined || cents === "") return "—";
  const n = Math.round(Number(cents) || 0);
  const list = currencies?.length ? currencies : AVAILABLE_CURRENCIES;
  if (list.length === 1) return formatCurrency(n / 100, list[0]);
  return `${centsToUnitsInput(n)} ${couponAmountSuffix(list)}`;
}

/** The unit after a coupon's money fields: "€" for one currency, "€ / zł" for several. */
export function couponAmountSuffix(currencies: string[] | null | undefined): string {
  const list = currencies?.length ? currencies : AVAILABLE_CURRENCIES;
  return [...new Set(list.map(currencySuffix))].join(" / ");
}

/** The coupon list's value column: "10 %", "3,00 €", "10 % v dobroimetju". */
export function adminCouponValueLabel(
  row: {
    type?: string;
    discount?: number;
    currencies?: string[] | null;
    rewardType?: string;
  },
  locale: string,
): string {
  if (!isCashbackCoupon(row)) {
    if (row.type === "PERCENTAGE")
      return couponValueLabel(row, () => "", locale);
    return couponMoneyLabel(row.discount, row.currencies);
  }
  const currency = toCashbackCurrency(row.currencies?.[0]);
  const fmt = (cents: number) => formatCurrency(cents / 100, currency);
  return `${couponValueLabel(row, fmt, locale)} v dobroimetju`;
}

/**
 * "Uporabljen kupon" on the thank-you page and the COD confirm page: the code
 * a receipt lists as used (never one a bundle blocked), with the cashback
 * suffix order detail shows, so a cashback coupon never reads as a discount.
 */
export function couponUsedLabel(order: unknown, suffix: string): string | null {
  if (!couponCodeAsUsed(order)) return null;
  return couponCodeWithSuffix(
    (order as { couponUsed?: unknown } | null | undefined)?.couponUsed,
    suffix,
  );
}

/**
 * A failed coupon save in the owner's words. The backend's class-validator
 * messages are English ("code must be a string, …"); a duplicate code is the
 * one an admin can fix by typing, so it is named.
 */
export function couponSaveErrorMessage(err: unknown, fallback: string): string {
  const data = (err as { response?: { status?: number; data?: { message?: unknown } } })
    ?.response;
  const raw = data?.data?.message;
  const text = Array.isArray(raw) ? raw.join(", ") : typeof raw === "string" ? raw : "";
  if (/duplicate|already exists|unique/i.test(text))
    return "Kupon s to kodo že obstaja. Izberite drugo kodo.";
  if (data?.status === 400 || /must be|should not|must not/i.test(text)) {
    const FIELDS: Record<string, string> = {
      code: "koda",
      discount: "popust",
      type: "vrsta",
      description: "opis",
      minimumAmount: "najnižji znesek",
      minimumAmountShipping: "najnižji znesek za dostavo",
      maximumDiscountAmount: "največji popust",
      validUntil: "velja do",
      currencies: "valute",
      cashbackLifetimeDays: "veljavnost dobroimetja",
    };
    const fields = [
      ...new Set(
        (Array.isArray(raw) ? raw : text.split(/,\s*/))
          .map((m) => String(m).trim().split(/\s+/)[0])
          .map((f) => FIELDS[f])
          .filter(Boolean),
      ),
    ];
    return fields.length
      ? `Strežnik je zavrnil kupon: preverite polja ${fields.join(", ")}.`
      : `${fallback} Strežnik je zavrnil podatke.`;
  }
  // Only a message already in Slovenian (the cashback rules' own) is shown as is.
  return /[čšžČŠŽ]/.test(text) ? text : fallback;
}

/**
 * Checkout applies a coupon whose list includes EUR (or that has no list) in
 * every shop, with the same number unconverted (backend
 * couponAppliesAtCheckout): a 40 € discount takes 40 zł in Poland. True when
 * a discount coupon's amounts will be read that way.
 */
export function couponAppliesInEveryCurrency(
  currencies: string[] | null | undefined,
): boolean {
  return (
    !currencies?.length ||
    currencies.some((c) => (c ?? "").toLowerCase() === "eur")
  );
}
